import { Link } from "react-router-dom";
import { formatRange, formatMoney } from "../lib/format";

/**
 * One row-rendering policy for every estimate list across admin.
 * Names are clickable: claimant -> profile, PWS -> profile, home -> profile, funder -> profile.
 */
export function EstimateTable({ rows, hide = [] }: { rows: any[]; hide?: Array<"claimant" | "pws" | "home" | "funder" | "type"> }) {
  const show = (col: string) => !hide.includes(col as any);
  if (!rows.length) return <div className="p-6 text-slate-500 text-sm">Nothing here.</div>;
  return (
    <div className="overflow-x-auto">
    <table className="w-full text-sm min-w-[640px]">
      <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
        <tr>
          <th className="text-left px-4 py-2">Ref</th>
          {show("type")     && <th className="text-left px-4 py-2">Type</th>}
          {show("claimant") && <th className="text-left px-4 py-2">Claimant</th>}
          {show("pws")      && <th className="text-left px-4 py-2">PWS</th>}
          {show("home")     && <th className="text-left px-4 py-2">Home</th>}
          {show("funder")   && <th className="text-left px-4 py-2">Funder</th>}
          <th className="text-left px-4 py-2">Dates</th>
          <th className="text-right px-4 py-2">Total</th>
          <th className="text-left px-4 py-2">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((e) => (
          <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50">
            <td className="px-4 py-2">
              <Link to={`/approvals/${e.id}`} className="text-liaise-blue hover:underline">{e.request_number}</Link>
            </td>
            {show("type") && <td className="px-4 py-2"><TypeBadge type={e.type ?? "holiday"} /></td>}
            {show("claimant") && (
              <td className="px-4 py-2">
                {e.claimant_entra_oid ? (
                  <Link to={`/claimant/${e.claimant_entra_oid}`} className="text-liaise-blue hover:underline">{e.claimant_display_name}</Link>
                ) : e.claimant_display_name}
              </td>
            )}
            {show("pws") && (
              <td className="px-4 py-2">
                {e.pws_id ? (
                  <Link to={`/pws/${e.pws_id}`} className="text-liaise-blue hover:underline">{e.pws_name ?? e.pws_initials}</Link>
                ) : (e.pws_initials ?? "-")}
              </td>
            )}
            {show("home") && (
              <td className="px-4 py-2">
                {e.home_id ? (
                  <Link to={`/home/${e.home_id}`} className="text-liaise-blue hover:underline">{e.home_name}</Link>
                ) : (e.home_name ?? "-")}
              </td>
            )}
            {show("funder") && (
              <td className="px-4 py-2">
                {e.funder_id ? (
                  <Link to={`/funder/${e.funder_id}`} className="text-liaise-blue hover:underline">{e.funder_name}</Link>
                ) : (e.funder_name ?? "-")}
              </td>
            )}
            <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{formatRange(e.start_date, e.end_date)}</td>
            <td className="px-4 py-2 text-right tabular-nums">{formatMoney(e.total_amount)}</td>
            <td className="px-4 py-2 text-xs">{e.status}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

export function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${highlight ? "border-liaise-orange bg-liaise-orange/5" : "border-slate-200 bg-white"}`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-liaise-navy tabular-nums">{value}</div>
    </div>
  );
}

export function TypeBadge({ type }: { type: string }) {
  const map: Record<string, { label: string; icon: string; classes: string }> = {
    holiday:     { label: "Holiday",      icon: "🏖", classes: "bg-liaise-blue/10 text-liaise-blue" },
    hospital:    { label: "Hospital",     icon: "🏥", classes: "bg-liaise-red/10 text-liaise-red" },
    transition:  { label: "Transition",   icon: "🤝", classes: "bg-liaise-green/15 text-emerald-800" },
    "case":      { label: "Case-by-case", icon: "📋", classes: "bg-liaise-purple/15 text-liaise-purple" },
    uplift:      { label: "Uplift",       icon: "📈", classes: "bg-liaise-yellow/30 text-amber-900" },
    awf:         { label: "AWF",          icon: "🎯", classes: "bg-liaise-orange/15 text-amber-900" },
  };
  const m = map[type] ?? { label: type, icon: "•", classes: "bg-slate-100 text-slate-700" };
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${m.classes}`}>
      <span>{m.icon}</span><span>{m.label}</span>
    </span>
  );
}

export function MetaGrid({ items }: { items: Array<[string, React.ReactNode]> }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
      {items.map(([label, value], i) => (
        <div key={i}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
          <div className="text-sm font-semibold text-liaise-navy mt-1">{value ?? "-"}</div>
        </div>
      ))}
    </div>
  );
}
